import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import {
  DocumentRevision,
  notifyHomologationItemLoadFailure,
  search,
  takeHomologationItem,
  type PaginatedSearchApiResult,
} from '@/entities/Document';
import { usePositionUserSelection } from '@/entities/User';
import api from '@/shared/api';
import {
  buildHomologationListRoute,
  formatCollaboratorNameAndRegistration,
  formatInstantToPtBR,
  useCustomToast,
  SearchParams,
  type ApiResponse,
  type InfoDocumento,
  type Pagination,
} from '@/shared/lib';

import { getApprovalDetailConfig } from '../config';
import { APPROVAL_ITEM_MESSAGES, DEFAULT_APPROVE_ERROR_MESSAGE } from '../config/messages';

type ApiErrorResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

export function getApprovalDocumentErrorMessage(
  err: unknown,
  fallback = DEFAULT_APPROVE_ERROR_MESSAGE
): string {
  if (!err || typeof err !== 'object' || !('response' in err)) {
    return fallback;
  }

  const data = (err as { response?: { data?: ApiErrorResponse } }).response?.data;

  if (!data) {
    return fallback;
  }

  const fieldErrors = data.errors;
  if (fieldErrors) {
    const trainingJustificationMessage = fieldErrors.trainingJustification?.[0];
    if (trainingJustificationMessage) {
      return trainingJustificationMessage;
    }

    const messages = Object.values(fieldErrors).flat().filter(Boolean);
    if (messages.length > 0) {
      return messages.join('; ');
    }
  }

  return data.message || fallback;
}

export default function useDocumentApproval() {
  const toast = useCustomToast();
  const router = useRouter();

  const {
    positions,
    users,
    selectedPositions,
    selectedUsers,
    positionsSelectedCount,
    usersSelectedCount,
    fetchPositionsAndUsers,
  } = usePositionUserSelection({ mode: 'by-selected-positions' });

  const selectedOption = ref<string | undefined>(undefined);
  const trainingOption = ref<string | undefined>(undefined);
  const showAdditionalField = ref(false);
  const reprovalJustification = ref<string | undefined>(undefined);
  const trainingJustification = ref<string | undefined>(undefined);
  const pendingDocument = ref<ApiResponse<DocumentRevision> | null>({
    success: false,
    data: undefined,
  });
  const pendingDocuments = ref<DocumentRevision[]>([]);
  const metadata = ref<Pagination>();
  const selectedApprovalPosition = ref<string | number | undefined>(undefined);
  const notes = ref<string | undefined>(undefined);
  const trainingSessions = ref<
    {
      trainingDate: string | null;
      responsibleMemberId: number | null;
      responsibleUserPositionId: number | null;
    }[]
  >([]);
  const invalidTrainingSession = ref(false);
  const approvalPositionOptions = ref<Array<{ id: number; name: string }>>([]);

  const isAdmin = localStorage.getItem('isAuthenticated'); // TODO: Update this to validate with admin user instead of logged in user

  const detailConfig = computed(() =>
    getApprovalDetailConfig(pendingDocument.value?.data?.document?.type)
  );

  watch([selectedOption, detailConfig], ([newOption, config]) => {
    showAdditionalField.value = newOption === 'aprovar' && config.hasTrainingStep;
  });

  const changelogLines = computed(
    () =>
      pendingDocument.value?.data?.changelogEntries?.map(
        (changeLogEntry) => changeLogEntry.description
      ) ?? []
  );

  const isSaveButtonActive = computed(() => {
    if (selectedOption.value === 'reprovar') {
      return !!reprovalJustification.value?.trim() && !!isAdmin;
    }

    if (selectedOption.value === 'aprovar' && !detailConfig.value.hasTrainingStep) {
      return !!selectedApprovalPosition.value && !!isAdmin;
    }

    if (selectedOption.value === 'aprovar' && trainingOption.value === 'nao') {
      return !!trainingJustification.value?.trim() && !!selectedApprovalPosition.value && !!isAdmin;
    }

    if (selectedOption.value === 'aprovar' && trainingOption.value === 'sim') {
      return (
        selectedPositions.value.length > 0 &&
        selectedUsers.value.length > 0 &&
        !invalidTrainingSession.value &&
        !!selectedApprovalPosition.value &&
        !!isAdmin
      );
    }

    return false;
  });

  watch(trainingSessions, (newSessions) => {
    invalidTrainingSession.value = newSessions.some(
      (session) =>
        !session.responsibleMemberId || !session.responsibleUserPositionId || !session.trainingDate
    );
  });

  const formatRevision = (revision: string) => {
    if (!revision) {
      return '';
    }

    revision = revision.length === 1 ? `0${revision}` : revision;
    revision = `Rev. ${revision}`;

    return revision;
  };

  const documentInfo = computed<InfoDocumento[]>(() => {
    const config = detailConfig.value;
    const document = pendingDocument.value?.data?.document;

    const info: InfoDocumento[] = [
      {
        label: 'Data da solicitação',
        value: formatInstantToPtBR(pendingDocument.value?.data?.createdAt),
      },
      {
        label: config.codeFieldLabel,
        value: document?.prefix ?? '',
      },
    ];

    if (config.showTitleField) {
      info.push({
        label: 'Título',
        value: document?.name ?? '',
      });
    }

    info.push(
      {
        label: 'Revisão',
        value: formatRevision(pendingDocument.value?.data?.revision?.toString() ?? '') ?? '',
      },
      {
        label: 'Elaboração',
        value: formatCollaboratorNameAndRegistration(
          document?.user?.name,
          document?.user?.registrationNumber
        ),
      }
    );

    return info;
  });

  const searchPendingDocuments = async (searchParams: SearchParams) => {
    try {
      const response = await search<PaginatedSearchApiResult<DocumentRevision>>(
        searchParams.searchUrl,
        searchParams
      );

      pendingDocuments.value = response.data;
      metadata.value = response.metadata;
    } catch {
      toast.showErrorToast({
        title: 'Erro ao buscar documentos pendentes',
        message: 'Tente novamente mais tarde.',
        type: 'error',
      });
    }
  };

  const fetchPendingDocumentIdData = async (revisionId: string, isRecord = false) => {
    try {
      // A natureza vem do segmento da rota, e nao de uma tentativa: o mesmo id existe nas duas
      // tabelas, entao pedir uma fonte e cair para a outra abriria o item errado.
      const [documentResponse] = await Promise.all([
        takeHomologationItem('approval', revisionId, isRecord),
        fetchPositionsAndUsers(),
      ]);
      pendingDocument.value = documentResponse;
    } catch (error) {
      console.error('Error fetching data:', error);
      pendingDocument.value = { success: false, message: 'Failed to fetch documents' };

      notifyHomologationItemLoadFailure(error, isRecord, APPROVAL_ITEM_MESSAGES);
      router.push(buildHomologationListRoute('/aprovacao', isRecord));
    }
  };

  const fetchUserPositions = async () => {
    try {
      const response = await api.get('/positions/token');

      const positionsData = response.data.data ?? response.data;

      if (positionsData && Array.isArray(positionsData) && positionsData.length > 0) {
        approvalPositionOptions.value = positionsData.map(
          (position: { positionId: number; positionName: string }) => ({
            id: position.positionId,
            name: position.positionName,
          })
        );
      } else {
        approvalPositionOptions.value = [];
      }
    } catch (error) {
      console.error('Erro ao buscar cargos do usuário autenticado:', error);
      approvalPositionOptions.value = [];
    }
  };

  const saveApproval = async () => {
    console.log('Saving approval', {
      option: selectedOption.value,
      training: trainingOption.value,
      positions: selectedPositions.value,
      users: selectedUsers.value,
    });
  };

  return {
    pendingDocument,
    pendingDocuments,
    selectedOption,
    trainingOption,
    showAdditionalField,
    positions,
    users,
    selectedPositions,
    selectedUsers,
    selectedApprovalPosition,
    changelogLines,
    detailConfig,
    isSaveButtonActive,
    positionsSelectedCount,
    usersSelectedCount,
    documentInfo,
    fetchPendingDocumentIdData,
    searchPendingDocuments,
    saveApproval,
    reprovalJustification,
    trainingJustification,
    formatRevision,
    notes,
    trainingSessions,
    approvalPositionOptions,
    fetchUserPositions,
    invalidTrainingSession,
    metadata,
  };
}
